import { Layout } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { DetailContainer } from '../../components/layout/DetailContainer';
import { Entity, InferenceFile } from '../../types';
import { MiniSidebar } from './MiniSidebar';
import { DocumentSplitResult } from './DocumentSplitResult';
import { EntityDetailView } from './EntityDetailView';
import { TopBar } from '../../components/layout/TopBar';
import pdfjs from 'pdfjs-dist/webpack';
import { Spinner } from '../../components/loading/Spinner';

const { Content } = Layout;

type Props = {
  inferenceFile: InferenceFile | null;
  goBack: () => any;
};

export const SplitOverview = ({ inferenceFile, goBack }: Props) => {
  const entities = inferenceFile && inferenceFile.inferenceResult.result.entities;

  const mainDocumentWrapper = useRef<HTMLDivElement>(null);
  const asideDocumentWrapper = useRef<HTMLDivElement>(null);
  const asideSectionMask = useRef<HTMLDivElement>(null);

  const [document, setDocument] = useState<any>();
  const [numPages, setNumPages] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  function onDocumentLoadSuccess({ numPages: nextNumPages }: any) {
    setNumPages(nextNumPages);
  }

  const [filterItemsCount, setFilterItemsCount] = useState<number>(5);
  const [detailEntity, setDetailEntity] = useState<{ entity: Entity; index: number } | null>(null);

  const [documentScrollHeight, setDocumentScrollHeight] = useState<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  const entityRefs = entities?.reduce((acc: any, value) => {
    acc[value.id as keyof {}] = React.createRef();
    return acc;
  }, {});

  function getBase64(file: any) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  useEffect(() => {
    if (inferenceFile) {
      const processPdf = async () => {
        try {
          setLoading(true);
          const document = await pdfjs.getDocument(inferenceFile.presignedUrl);
          const data = await (document as any).getData();
          const blob = new Blob([data as any], { type: 'application/pdf' });
          const base64 = await getBase64(blob);
          setDocument(base64);
          setError(false);
        } catch (e) {
          setDocument(null);
          setError(true);
          console.log(e);
        } finally {
          setLoading(false);
        }
      };
      processPdf();
    }
  }, [inferenceFile]);

  const handleClick = (id: string) => {
    entityRefs[id].current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <>
      <TopBar
        title={`Document Splitter Result: ${inferenceFile?.fileName}`}
        closeFunction={(event: any) => {
          event.preventDefault();
          event.stopPropagation();
          goBack();
        }}
      />
      {loading && <Spinner />}
      {entities?.length && document && !loading && !error ? (
        <DetailContainer>
          <MiniSidebar
            file={document}
            mainDocumentWrapper={mainDocumentWrapper}
            asideDocumentWrapper={asideDocumentWrapper}
            asideSectionMask={asideSectionMask}
            entities={entities}
            filterItemsCount={filterItemsCount}
            setFilterItemsCount={setFilterItemsCount}
            handleClick={handleClick}
            documentScrollHeight={documentScrollHeight}
            setDocumentScrollHeight={setDocumentScrollHeight}
            onDocumentLoadSuccess={onDocumentLoadSuccess}
            numPages={numPages}
          />
          <Layout>
            <Content>
              <DocumentSplitResult
                file={document}
                mainDocumentWrapper={mainDocumentWrapper}
                asideSectionMask={asideSectionMask}
                entities={entities}
                filterItemsCount={filterItemsCount}
                setFilterItemsCount={setFilterItemsCount}
                entityRefs={entityRefs}
                documentScrollHeight={documentScrollHeight}
                setDocumentScrollHeight={setDocumentScrollHeight}
                setDetailEntity={setDetailEntity}
                numPages={numPages}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
              />
            </Content>
          </Layout>
        </DetailContainer>
      ) : null}
      {!loading && error && (
        <div className="flex justify-center items-center p-8">An error has occured while loading the document.</div>
      )}
      {detailEntity ? (
        <EntityDetailView
          detailEntity={detailEntity}
          file={document}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          onCancel={() => {
            setDetailEntity(null);
          }}
        />
      ) : null}
    </>
  );
};
